/**
 * Table desktop chrome rails (no wallet duplication):
 * LEFT  — Account · Active tables · Friends + chat/log
 * RIGHT — Full wallet stack (play/chain/connect/fund/cash-out) + wait list + ad
 */
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  User,
  Users,
  LayoutGrid,
  Star,
  LogIn,
  ExternalLink,
  Wallet,
  RefreshCw,
  Loader2,
  ArrowDownToLine,
  Mail,
  Copy,
  CheckCircle2,
  Unplug,
} from "lucide-react";
import { useWalletState } from "@/contexts/wallet-context";
import {
  isLoggedIn,
  loginWithGrudgeId,
  openGrudgeWalletSite,
} from "@/lib/grudge-auth";
import { formatPlayGbux } from "@shared/gbux-math";
import { FLEET } from "@shared/fleet";
import type { OpenTableEntry } from "@/lib/open-tables";
import {
  addFriend,
  listFriends,
  removeFriend,
  type FriendEntry,
} from "@/lib/friends";
import { lookupPlayUser } from "@/lib/play-transfer-api";
import { TableChatLog } from "@/components/table-chat-log";
import { ConnectRewardsModal } from "@/components/connect-rewards-modal";
import {
  transferAndCreditPlay,
  PLAY_DEPOSIT_SSOT,
} from "@/lib/gbux-transfer";
import type {
  ChatMessageEvent,
  LastAction,
} from "@/hooks/use-poker-websocket";
import type { SolanaWalletId } from "@/lib/solana-wallets";
import { useToast } from "@/hooks/use-toast";
import { BUDB_CHARACTER } from "@shared/budb-character";

type LeftTab = "account" | "tables" | "friends";
type RightTab = "wallets" | "wait";

function shortAddr(a: string | null | undefined) {
  if (!a || a.length < 12) return a || "—";
  return `${a.slice(0, 4)}…${a.slice(-4)}`;
}

// ── LEFT ─────────────────────────────────────────────────────────────────────

export function TableLeftRail(props: {
  tableId: string;
  actionOnUs: boolean;
  lastAction: LastAction | null;
  chatMessages?: ChatMessageEvent[];
  selfWallet?: string | null;
  communityCards?: Array<{ rank: string; suit: string }>;
  phase?: string;
  handResult?: string | null;
  sendChat?: (text: string) => void;
  wsConnected?: boolean;
  openTables: OpenTableEntry[];
  onRefreshOpen: () => void;
}) {
  const [, setLocation] = useLocation();
  const { wallet, loginGrudgeId, refreshBalances } = useWalletState();
  const [tab, setTab] = useState<LeftTab>("tables");
  const [friends, setFriends] = useState<FriendEntry[]>(() => listFriends());
  const [friendInput, setFriendInput] = useState("");
  const [friendBusy, setFriendBusy] = useState(false);
  const [friendErr, setFriendErr] = useState<string | null>(null);
  const { toast } = useToast();

  const play = wallet.playGbux || wallet.gbuxBalance || 0;
  const pk = wallet.publicKey;

  const onAddFriend = async () => {
    setFriendBusy(true);
    setFriendErr(null);
    try {
      const r = await lookupPlayUser(friendInput);
      if (!r.success) {
        setFriendErr(r.error || "Not found");
        // Still allow pin by username without lookup
        if (friendInput.trim().length >= 2) {
          setFriends(addFriend(friendInput));
          setFriendInput("");
        }
        return;
      }
      setFriends(addFriend(r.username || friendInput, r.walletHint));
      setFriendInput("");
      toast({ title: "Friend added", description: `@${r.username}` });
    } catch (e) {
      setFriendErr(e instanceof Error ? e.message : "Lookup failed");
    } finally {
      setFriendBusy(false);
    }
  };

  return (
    <aside className="poker-rail poker-rail--left" aria-label="Account, tables, friends">
      <div className="poker-rail-tabs">
        {(
          [
            ["account", "Account", User],
            ["tables", "Tables", LayoutGrid],
            ["friends", "Friends", Users],
          ] as const
        ).map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            data-active={tab === id}
            className="poker-rail-tab"
            onClick={() => setTab(id)}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      <div className="poker-rail-panel" style={{ flex: 1, minHeight: 0 }}>
        {tab === "account" && (
          <div className="poker-rail-scroll space-y-2">
            <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/25 p-2.5">
              <div className="text-[10px] uppercase text-emerald-400/80 font-bold">
                Play GBUX
              </div>
              <div className="text-xl font-black text-emerald-300 tabular-nums">
                {formatPlayGbux(play)}
              </div>
              <div className="text-[10px] text-zinc-500">
                Server wallet · tables/slots/BJ
              </div>
            </div>
            <div className="text-[11px] text-zinc-400 space-y-1">
              <div>
                <span className="text-zinc-500">Wallet </span>
                <span className="font-mono text-zinc-200">{shortAddr(pk)}</span>
              </div>
              <div>
                <span className="text-zinc-500">Grudge ID </span>
                <span className={isLoggedIn() ? "text-emerald-400" : "text-amber-400"}>
                  {isLoggedIn() ? "signed in" : "not signed in"}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="poker-rail-btn"
              onClick={() => setLocation("/account")}
            >
              Open full account
            </button>
            {!isLoggedIn() ? (
              <button
                type="button"
                className="poker-rail-btn poker-rail-btn--amber"
                onClick={() => loginGrudgeId("/account")}
              >
                <LogIn size={12} /> Grudge ID / Puter
              </button>
            ) : (
              <button
                type="button"
                className="poker-rail-btn"
                onClick={() => openGrudgeWalletSite("/account")}
              >
                <ExternalLink size={12} /> Grudge Wallet site
              </button>
            )}
            <button
              type="button"
              className="poker-rail-btn poker-rail-btn--ghost"
              onClick={() => void refreshBalances()}
            >
              <RefreshCw size={12} /> Refresh balances
            </button>
            <p className="text-[10px] text-zinc-600 leading-snug px-0.5">
              Wallet connect / fund / multi-wallet live on the{" "}
              <strong className="text-zinc-400">right rail</strong> — no duplicate
              wallet panel here.
            </p>
          </div>
        )}

        {tab === "tables" && (
          <div className="poker-rail-scroll">
            <div className="poker-rail-panel__title" style={{ border: 0, padding: "4px 2px 8px" }}>
              <span>Active tables</span>
              <span style={{ opacity: 0.5 }}>{props.openTables.length}</span>
            </div>
            {props.openTables.length === 0 && (
              <p className="text-[11px] text-zinc-500 px-1">
                Tables you sit appear here. When action is on you at another
                table, that tab pulses.
              </p>
            )}
            {props.openTables.map((t) => {
              const active = t.tableId === props.tableId;
              const action = active ? props.actionOnUs : Boolean(t.actionOnUs);
              return (
                <button
                  key={t.tableId}
                  type="button"
                  className={`poker-open-tab${active ? " poker-open-tab--active" : ""}${
                    action ? " poker-open-tab--action" : ""
                  }`}
                  onClick={() => setLocation(`/table/${t.tableId}`)}
                >
                  <span style={{ fontWeight: 800, fontSize: 12 }}>{t.name}</span>
                  <span style={{ fontSize: 10, color: "rgba(148,163,184,0.95)" }}>
                    {t.smallBlind}/{t.bigBlind}
                    {t.players != null
                      ? ` · ${t.players}/${t.maxPlayers ?? "?"}`
                      : ""}
                    {action ? " · YOUR TURN" : ""}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              className="poker-rail-btn poker-rail-btn--ghost"
              style={{ marginTop: 8 }}
              onClick={() => setLocation("/lobby")}
            >
              Lobby · join more
            </button>
          </div>
        )}

        {tab === "friends" && (
          <div className="poker-rail-scroll space-y-2">
            <p className="text-[10px] text-zinc-500">
              Pin players by username (play ledger). Send GBUX from Account →
              Send.
            </p>
            <div className="flex gap-1">
              <input
                value={friendInput}
                onChange={(e) => setFriendInput(e.target.value)}
                placeholder="@username"
                className="poker-rail-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") void onAddFriend();
                }}
              />
              <button
                type="button"
                className="poker-rail-btn poker-rail-btn--amber"
                style={{ flex: "0 0 auto", padding: "6px 10px" }}
                disabled={friendBusy}
                onClick={() => void onAddFriend()}
              >
                {friendBusy ? <Loader2 size={12} className="animate-spin" /> : "Add"}
              </button>
            </div>
            {friendErr && (
              <p className="text-[10px] text-amber-400/90">{friendErr}</p>
            )}
            {friends.map((f) => (
              <div key={f.username} className="poker-wait-row">
                <div>
                  <div className="font-semibold text-zinc-100">@{f.username}</div>
                  {f.walletHint && (
                    <div className="text-[10px] text-zinc-500 font-mono">
                      {f.walletHint}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="text-[10px] text-red-300/80 hover:text-red-200"
                  onClick={() => setFriends(removeFriend(f.username))}
                >
                  Remove
                </button>
              </div>
            ))}
            {friends.length === 0 && (
              <p className="text-[11px] text-zinc-600">No friends pinned yet.</p>
            )}
          </div>
        )}
      </div>

      <TableChatLog
        tableId={props.tableId}
        lastAction={props.lastAction}
        chatMessages={props.chatMessages}
        selfWallet={props.selfWallet}
        communityCards={props.communityCards}
        phase={props.phase}
        handResult={props.handResult}
        sendChat={props.sendChat}
        connected={props.wsConnected}
        budbVoice={props.tableId === BUDB_CHARACTER.vsTableId}
      />
    </aside>
  );
}

// ── RIGHT — wallets only ─────────────────────────────────────────────────────

export function TableRightRail(props: {
  players: Array<{
    id?: string;
    username?: string;
    chips?: number;
    isActive?: boolean;
  }>;
  maxPlayers: number;
}) {
  const {
    wallet,
    connect,
    disconnect,
    refreshBalances,
    ensureSigner,
    getConnectedWallet,
    canSign,
    detectedWallets,
    rescanWallets,
  } = useWalletState();
  const multiWallets = wallet.multiWallets || [];
  const { toast } = useToast();
  const [tab, setTab] = useState<RightTab>("wallets");
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [waitList, setWaitList] = useState<string[]>([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<"idle" | "signing" | "crediting" | "done">(
    "idle",
  );

  useEffect(() => {
    rescanWallets();
    void refreshBalances();
    try {
      const raw = sessionStorage.getItem("budb_table_waitlist");
      if (raw) setWaitList(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
  }, [rescanWallets, refreshBalances]);

  const play = wallet.playGbux || wallet.gbuxBalance || 0;
  const chain = Number(wallet.chainGbux || 0);
  const sol = Number(wallet.chainSol || wallet.solBalance || 0);
  const pk = wallet.publicKey;
  const seated = props.players.filter((p) => p?.id);
  const openSeats = Math.max(0, props.maxPlayers - seated.length);

  const onConnect = async (id?: SolanaWalletId) => {
    setBusy(true);
    try {
      await connect(id);
      toast({ title: "Wallet connected" });
      await refreshBalances();
    } catch (e) {
      toast({
        title: "Connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const onFundPlay = useCallback(async () => {
    const amt = Math.floor(Number(amount));
    if (!pk || amt < 1) {
      toast({
        title: "Enter GBUX amount",
        description: "Fund play from chain GBUX SPL",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    setStep("signing");
    try {
      let w = getConnectedWallet();
      if (!w) w = await ensureSigner({ silentOnly: false });
      if (!w) throw new Error("Reconnect Phantom / Solflare to sign");
      setStep("crediting");
      const r = await transferAndCreditPlay({
        wallet: w,
        amountHuman: amt,
        depositTo: PLAY_DEPOSIT_SSOT.agent,
      });
      setStep("done");
      toast({
        title: "Play funded",
        description: r.message || `+${amt} play GBUX`,
      });
      setAmount("");
      await refreshBalances();
    } catch (e) {
      setStep("idle");
      toast({
        title: "Fund failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setStep("idle"), 1500);
    }
  }, [
    amount,
    pk,
    getConnectedWallet,
    ensureSigner,
    refreshBalances,
    toast,
  ]);

  const joinWait = () => {
    const next = [...waitList, `Wait #${waitList.length + 1}`].slice(0, 12);
    setWaitList(next);
    try {
      sessionStorage.setItem("budb_table_waitlist", JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  return (
    <aside className="poker-rail poker-rail--right" aria-label="Wallets and wait list">
      <div className="poker-rail-tabs">
        <button
          type="button"
          className="poker-rail-tab"
          data-active={tab === "wallets"}
          onClick={() => setTab("wallets")}
        >
          <Wallet size={12} /> Wallets
        </button>
        <button
          type="button"
          className="poker-rail-tab"
          data-active={tab === "wait"}
          onClick={() => setTab("wait")}
        >
          <Users size={12} /> Wait
        </button>
      </div>

      {tab === "wallets" && (
        <div className="poker-rail-panel" style={{ flex: 1, minHeight: 0 }}>
          <div className="poker-rail-scroll space-y-2">
            {/* Balances */}
            <div className="grid grid-cols-1 gap-1.5">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-950/30 p-2">
                <div className="text-[9px] uppercase font-bold text-emerald-400/90">
                  Play (server)
                </div>
                <div className="text-lg font-black text-emerald-300 tabular-nums">
                  {formatPlayGbux(play)}
                </div>
              </div>
              <div className="rounded-lg border border-cyan-500/25 bg-cyan-950/20 p-2">
                <div className="text-[9px] uppercase font-bold text-cyan-400/90">
                  Chain wallet
                </div>
                <div className="text-sm font-bold text-cyan-100 tabular-nums">
                  {formatPlayGbux(chain)} GBUX
                </div>
                <div className="text-[10px] text-zinc-500">
                  {sol.toFixed(4)} SOL · {shortAddr(pk)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 text-[10px]">
              {canSign || getConnectedWallet() ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 size={11} /> Signer ready
                </span>
              ) : (
                <span className="text-amber-400">Signer needed for fund</span>
              )}
              <button
                type="button"
                className="ml-auto text-zinc-500 hover:text-zinc-300"
                onClick={() => void refreshBalances()}
                title="Refresh"
              >
                <RefreshCw size={12} />
              </button>
            </div>

            {/* Connect */}
            {!pk ? (
              <div className="space-y-1">
                <p className="text-[10px] text-zinc-500">
                  Connect inject wallet (Phantom first) — no auto-connect.
                </p>
                {(detectedWallets.filter((w) => w.available).length
                  ? detectedWallets.filter((w) => w.available)
                  : [{ id: "phantom" as SolanaWalletId, name: "Phantom", available: true }]
                ).map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    className="poker-rail-btn poker-rail-btn--amber"
                    disabled={busy}
                    onClick={() => void onConnect(w.id as SolanaWalletId)}
                  >
                    <Wallet size={12} /> {w.name || w.id}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="poker-rail-btn flex-1"
                    onClick={async () => {
                      await ensureSigner({ silentOnly: false });
                      toast({ title: "Signer re-bound" });
                    }}
                  >
                    Rebind sign
                  </button>
                  <button
                    type="button"
                    className="poker-rail-btn poker-rail-btn--ghost"
                    title="Copy address"
                    onClick={() => {
                      void navigator.clipboard.writeText(pk);
                      toast({ title: "Address copied" });
                    }}
                  >
                    <Copy size={12} />
                  </button>
                  <button
                    type="button"
                    className="poker-rail-btn poker-rail-btn--ghost"
                    title="Disconnect"
                    onClick={() => void disconnect()}
                  >
                    <Unplug size={12} />
                  </button>
                </div>
                {multiWallets.length > 1 && (
                  <div className="text-[10px] text-zinc-500">
                    Multi-wallet: {multiWallets.length} addresses in registry
                  </div>
                )}
              </div>
            )}

            {/* Fund play */}
            <div className="rounded-lg border border-amber-500/25 bg-amber-950/15 p-2 space-y-1.5">
              <div className="text-[10px] font-bold text-amber-200 flex items-center gap-1">
                <ArrowDownToLine size={12} /> Fund play (chain → server)
              </div>
              <input
                className="poker-rail-input"
                type="number"
                min={1}
                placeholder="GBUX amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <button
                type="button"
                className="poker-rail-btn poker-rail-btn--amber"
                disabled={busy || !pk}
                onClick={() => void onFundPlay()}
              >
                {busy ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  "Transfer & credit"
                )}
              </button>
              {step !== "idle" && step !== "done" && (
                <p className="text-[10px] text-amber-100/80">
                  {step === "signing" && "Approve in wallet…"}
                  {step === "crediting" && "Crediting play ledger…"}
                </p>
              )}
              <p className="text-[9px] text-zinc-600 leading-snug">
                SPL → BUDBai · edge verifies · 1:1 play. Not a player P2P send.
              </p>
            </div>

            {/* Cash out */}
            <a
              className="poker-rail-btn poker-rail-btn--ghost"
              href={`${FLEET.supportMailto}?subject=BUDB%20cash-out%20/%20swap&body=Wallet:%20${encodeURIComponent(pk || "")}%0APlay:%20${play}`}
            >
              <Mail size={12} /> Cash out / swap → email staff
            </a>

            {!isLoggedIn() && (
              <button
                type="button"
                className="poker-rail-btn"
                onClick={() => loginWithGrudgeId("/lobby")}
              >
                <LogIn size={12} /> Grudge ID (fleet bag)
              </button>
            )}
          </div>
        </div>
      )}

      {tab === "wait" && (
        <div className="poker-rail-panel" style={{ flex: 1, minHeight: 0 }}>
          <div className="poker-rail-panel__title">
            <span>Wait list</span>
            <span style={{ opacity: 0.55 }}>{openSeats} open</span>
          </div>
          <div className="poker-rail-scroll">
            {seated.map((p, i) => (
              <div key={p.id || i} className="poker-wait-row">
                <span>{(p.username || p.id || "Player").slice(0, 14)}</span>
                <span style={{ color: "#4ade80" }}>
                  {p.chips != null ? Number(p.chips).toFixed(0) : "—"}
                </span>
              </div>
            ))}
            {waitList.map((w, i) => (
              <div key={`w-${i}`} className="poker-wait-row">
                <span style={{ color: "#fbbf24" }}>{w}</span>
                <span style={{ opacity: 0.6 }}>waiting</span>
              </div>
            ))}
            <button type="button" className="poker-rail-btn" onClick={joinWait}>
              Join wait list
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className="poker-ad-card"
        onClick={() => setRewardsOpen(true)}
        data-testid="table-promo-ad"
      >
        <img
          src="/media/ads/budb-promo.png"
          alt="BUDB PKR — free GBUX"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
        <div className="poker-ad-pulse">FREE 200 $GBUX</div>
      </button>
      <ConnectRewardsModal open={rewardsOpen} onOpenChange={setRewardsOpen} />
    </aside>
  );
}
